/**
 * Blog URL Slug Translations
 * Maps base slugs (English folder names) to localized URL slugs
 * 
 * SEO Strategy: Solution-first, search-intent driven URLs
 * Focus: Industry + Solution (hotel-kiosk, not digital-platform)
 */

import { Locale } from './i18n';

export const BLOG_SLUG_TRANSLATIONS = {
  'hotel-digital-ordering': {
    en: 'hotel-digital-ordering', // Keep EN unchanged for now
    nl: 'hotel-kiosk-roomservice',
    de: 'hotel-kiosk-zimmerservice',
  },
  'self-order-kiosks-for-hospitality': {
    en: 'self-order-kiosks-for-hospitality', // Keep EN unchanged
    nl: 'bestelzuil-horeca',
    de: 'selbstbedienungs-kiosk-gastronomie',
  },
  'qr-ordering-restaurant-guide': {
    en: 'qr-ordering-restaurant-guide', // Keep EN unchanged
    nl: 'qr-bestellen-restaurant',
    de: 'qr-bestellung-restaurant',
  },
  'all-you-can-eat-ordering-system': {
    en: 'all-you-can-eat-ordering-system', // Keep EN unchanged
    nl: 'all-you-can-eat-bestelsysteem',
    de: 'all-you-can-eat-bestellsystem',
  },
  'golf-clubs-digital-ordering': {
    en: 'golf-clubs-digital-ordering', // Keep EN unchanged
    nl: 'golfclub-digitaal-bestellen',
    de: 'golfclub-digitale-bestellung',
  },
  'bowling-alley-digital-ordering': {
    en: 'bowling-alley-digital-ordering', // Keep EN unchanged
    nl: 'bowlingbaan-bestelsysteem',
    de: 'bowlingbahn-bestellsystem',
  },
  'bakery-ordering-software': {
    en: 'bakery-ordering-software', // Keep EN unchanged
    nl: 'bakkerij-bestelsysteem',
    de: 'baeckerei-bestellsystem',
  },
  'restaurant-kpi-guide': {
    en: 'restaurant-kpi-guide', // Keep EN unchanged
    nl: 'restaurant-kpi-gids',
    de: 'restaurant-kpi-leitfaden',
  },
  'choosing-ordering-solution': {
    en: 'choosing-ordering-solution', // Keep EN unchanged
    nl: 'bestelsysteem-kiezen',
    de: 'bestellsystem-waehlen',
  },
  'snackbar-digital-ordering': {
    en: 'snackbar-digital-ordering', // Keep EN unchanged
    nl: 'snackbar-bestelsysteem',
    de: 'snackbar-bestellsystem',
  },
  'the-new-hospitality-industry': {
    en: 'the-new-hospitality-industry', // Keep EN unchanged
    nl: 'nieuwe-horeca-trends',
    de: 'neue-gastronomie-trends',
  },
  'the-psychology-behind-upselling': {
    en: 'the-psychology-behind-upselling', // Keep EN unchanged
    nl: 'psychologie-upselling',
    de: 'psychologie-zusatzverkauf',
  },
} as const;

export type BaseSlug = keyof typeof BLOG_SLUG_TRANSLATIONS;

/**
 * Get localized slug for a given base slug and locale
 */
export function getLocalizedSlug(baseSlug: string, locale: Locale): string {
  const translations = BLOG_SLUG_TRANSLATIONS[baseSlug as BaseSlug];
  return translations?.[locale] || baseSlug;
}

/**
 * Resolve any slug (localized or base) back to base slug
 * Handles both base slugs and localized slugs for robustness
 */
export function getBaseSlug(slug: string): BaseSlug | null {
  // First check if it's already a base slug
  if (slug in BLOG_SLUG_TRANSLATIONS) {
    return slug as BaseSlug;
  }
  
  // Otherwise search through translations
  for (const [base, translations] of Object.entries(BLOG_SLUG_TRANSLATIONS)) {
    const slugs = Object.values(translations) as string[];
    if (slugs.includes(slug)) {
      return base as BaseSlug;
    }
  }
  
  return null;
}

/**
 * Get all localized slug variants for a base slug
 */
export function getAllLocalizedSlugs(baseSlug: BaseSlug): string[] {
  const translations = BLOG_SLUG_TRANSLATIONS[baseSlug];
  return translations ? Object.values(translations) : [];
}
